/**
 * "Tambah sumber" — the one place a notebook gains sources.
 *
 * A DIALOG, not a panel. The notebook's own panel shows what is IN it; the world of things
 * that could be added is a separate act, opened deliberately and closed again. The first
 * attempt at this put the whole corpus's collection list permanently in the rail, which
 * meant the reader was always looking at 50 collections they had not chosen — the notebook
 * disappeared behind its own picker.
 *
 * Three ways in, because they answer three different questions:
 *
 *   SELURUH PUSTAKA  "everything the organisation has" — one row, one click. Live, so it
 *                    keeps meaning everything as the corpus grows.
 *   KOLEKSI          "a region's documents" — the whole collection, also live.
 *   DOKUMEN          "this specific regulation" — title search plus the region and year
 *                    filters, because 3,331 titles cannot be scrolled.
 *
 * NOTHING IS SAVED UNTIL "Tambah". The dialog holds a draft, so a reader can tick, untick
 * and change their mind without each keystroke becoming a stored source set — and cancelling
 * genuinely cancels.
 */
import * as Dialog from "@radix-ui/react-dialog";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { FacetSelect } from "@/components/FacetSelect.tsx";
import { Button, Input } from "@/components/primitives.tsx";
import { formatCount } from "@/i18n/index.ts";
import { api } from "@/lib/api.ts";
import type { Collection } from "@/lib/api-types.ts";
import { parseLegalTitle, truncateAtWord } from "@/lib/legalTitle.ts";

export interface SourceSelection {
  wholeCorpus: boolean;
  collectionIds: string[];
  documentIds: string[];
}

export function AddSourcesDialog({
  open,
  onOpenChange,
  collections,
  corpusTotal,
  current,
  onAdd,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collections: Collection[];
  corpusTotal: number;
  /** What the notebook already holds, so nothing is offered twice. */
  current: SourceSelection;
  onAdd: (selection: SourceSelection) => void;
}) {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState<"collections" | "documents">("collections");
  const [search, setSearch] = useState("");
  const [wilayah, setWilayah] = useState("");
  const [year, setYear] = useState("");
  // The DRAFT. Empty every time the dialog opens: it is what to ADD, not the notebook's
  // whole set, so showing existing sources as ticked would invite un-ticking them here and
  // that is the panel's job.
  const [draft, setDraft] = useState<SourceSelection>({
    wholeCorpus: false,
    collectionIds: [],
    documentIds: [],
  });

  const documents = useQuery({
    queryKey: ["corpus", "add-sources", search, wilayah, year],
    queryFn: () =>
      api.corpus({
        limit: 50,
        search: search || undefined,
        wilayah: wilayah || undefined,
        year: year || undefined,
      }),
    enabled: open && tab === "documents",
  });

  // Keyed on the SEARCH BOX too, exactly as the library page is. Without it the counts
  // beside each region and year stayed at their whole-corpus values while the list below
  // narrowed — so typing "qanun" left "Bireuen 347" on screen next to a list of eight, and
  // picking that region produced eight, not 347. The list was always right; only the
  // numbers offering it were stale, which is the worse half to get wrong: a count is a
  // promise about what a click will return.
  //
  // The empty string in third position is `status`, which this picker does not expose.
  const facets = useQuery({
    queryKey: ["corpus", "facets", search, "", year, wilayah],
    queryFn: () =>
      api.corpusFacets({
        search: search || undefined,
        year: year || undefined,
        wilayah: wilayah || undefined,
      }),
    enabled: open && tab === "documents",
  });

  const count =
    draft.collectionIds.length + draft.documentIds.length + (draft.wholeCorpus ? 1 : 0);

  const reset = () => {
    setDraft({ wholeCorpus: false, collectionIds: [], documentIds: [] });
    setSearch("");
    setWilayah("");
    setYear("");
    setTab("collections");
  };

  const toggle = (key: "collectionIds" | "documentIds", id: string) =>
    setDraft((previous) => ({
      ...previous,
      [key]: previous[key].includes(id)
        ? previous[key].filter((value) => value !== id)
        : [...previous[key], id],
    }));

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        // Closing DISCARDS the draft. A dialog that quietly kept half a selection would
        // add sources the reader thought they had abandoned.
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="dialog add-sources" aria-describedby={undefined}>
          <Dialog.Title className="dialog-title">{t("addSources.title")}</Dialog.Title>

          {/* All of Pustaka sits ABOVE the tabs: it is not one collection among fifty, it
              is the whole thing, and burying it in a list would make the commonest action
              the hardest to find. */}
          <button
            type="button"
            className={`add-whole${draft.wholeCorpus ? " is-picked" : ""}`}
            disabled={current.wholeCorpus}
            onClick={() => setDraft((p) => ({ ...p, wholeCorpus: !p.wholeCorpus }))}
          >
            <span>
              <strong>{t("addSources.wholeCorpus")}</strong>
              <span className="add-whole-sub">
                {current.wholeCorpus
                  ? t("addSources.alreadyAdded")
                  : t("addSources.wholeCorpusSub", {
                      total: formatCount(corpusTotal, i18n.language),
                    })}
              </span>
            </span>
            <span className="add-tick">{draft.wholeCorpus ? "✓" : "+"}</span>
          </button>

          <div className="add-tabs" role="tablist">
            {(["collections", "documents"] as const).map((value) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={tab === value}
                className={`add-tab${tab === value ? " is-active" : ""}`}
                onClick={() => setTab(value)}
              >
                {t(`addSources.${value}`)}
              </button>
            ))}
          </div>

          {tab === "collections" ? (
            <div className="add-list">
              {collections.map((collection) => {
                const already = current.collectionIds.includes(collection.id);
                const picked = draft.collectionIds.includes(collection.id);
                return (
                  <button
                    key={collection.id}
                    type="button"
                    className={`add-row${picked ? " is-picked" : ""}`}
                    disabled={already}
                    onClick={() => toggle("collectionIds", collection.id)}
                  >
                    <span className="add-row-name">{collection.name}</span>
                    <span className="add-row-count">
                      {already
                        ? t("addSources.alreadyAdded")
                        : formatCount(collection.document_count, i18n.language)}
                    </span>
                    <span className="add-tick">{picked ? "✓" : "+"}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <>
              <div className="add-filters">
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={t("library.search")}
                  aria-label={t("library.search")}
                />
                <FacetSelect
                  label={t("library.columns.region")}
                  allLabel={t("library.allRegions")}
                  value={wilayah}
                  options={facets.data?.regions ?? []}
                  onChange={setWilayah}
                />
                <FacetSelect
                  label={t("library.columns.year")}
                  allLabel={t("library.allYears")}
                  value={year}
                  options={facets.data?.years ?? []}
                  onChange={setYear}
                />
              </div>
              <div className="add-list">
                {(documents.data?.documents ?? []).map((document) => {
                  const already = current.documentIds.includes(document.id);
                  const picked = draft.documentIds.includes(document.id);
                  const { label, subject, full } = parseLegalTitle(document.title);
                  return (
                    <button
                      key={document.id}
                      type="button"
                      className={`add-row${picked ? " is-picked" : ""}`}
                      disabled={already}
                      title={full}
                      onClick={() => toggle("documentIds", document.id)}
                    >
                      <span className="add-row-doc">
                        {label ? <span className="mono add-row-label">{label}</span> : null}
                        <span>{truncateAtWord(subject, 78)}</span>
                      </span>
                      <span className="add-tick">
                        {already ? t("addSources.alreadyAdded") : picked ? "✓" : "+"}
                      </span>
                    </button>
                  );
                })}
                {documents.data && documents.data.documents.length === 0 ? (
                  <p className="add-empty">{t("addSources.noDocuments")}</p>
                ) : null}
                {/* Says what is NOT shown. A list capped at 50 out of 3,331 that stayed
                    silent would read as "this is everything there is". */}
                {documents.data && documents.data.total > documents.data.documents.length ? (
                  <p className="add-empty">
                    {t("addSources.moreExist", {
                      shown: formatCount(documents.data.documents.length, i18n.language),
                      total: formatCount(documents.data.total, i18n.language),
                    })}
                  </p>
                ) : null}
              </div>
            </>
          )}

          <div className="dialog-foot">
            <Dialog.Close asChild>
              <Button>{t("common.cancel")}</Button>
            </Dialog.Close>
            <Button
              variant="primary"
              disabled={count === 0}
              onClick={() => {
                onAdd(draft);
                reset();
                onOpenChange(false);
              }}
            >
              {t("addSources.add", { count })}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Merge what the dialog returned into what the notebook already holds. */
export function mergeSelection(
  current: SourceSelection,
  added: SourceSelection,
): SourceSelection {
  const unique = (values: string[]) => [...new Set(values)];
  return {
    wholeCorpus: current.wholeCorpus || added.wholeCorpus,
    collectionIds: unique([...current.collectionIds, ...added.collectionIds]),
    documentIds: unique([...current.documentIds, ...added.documentIds]),
  };
}
